<template>
  <iColumn gap="extraLarge">
    <iText v-if="mfaRequired && enrolledMethods.length === 0" variant="error">
      Your account requires two-factor authentication. Please set up at least one method below.
    </iText>
    <iText v-else-if="!canRemoveFactor" variant="subtle">
      Your account requires two-factor authentication, so this method can only be removed once
      another one is set up.
    </iText>
    <iText v-if="errorMessage" variant="error">
      {{ errorMessage }}
    </iText>

    <iColumn gap="standard">
      <iLabel>Authenticator app (TOTP)</iLabel>
      <iRow vertical-align="middle" wrap="nowrap">
        <iText v-if="totpEnabled" variant="success">
          Enabled
        </iText>
        <iText v-else variant="subtle">
          Not enabled
        </iText>
      </iRow>
      <iRow v-tooltip="factorLockedReason" width="hug">
        <iButton
          v-if="!totpEnabled"
          variant="primary"
          :is-loading="isLoading"
          @click="openTotpModal"
        >
          Enable authenticator app
        </iButton>
        <iButton
          v-else
          variant="secondary"
          :is-loading="isLoading"
          :disabled="!canRemoveFactor"
          @click="disableAuthenticatorApp"
        >
          Disable
        </iButton>
      </iRow>
    </iColumn>

    <iColumn gap="standard">
      <iLabel>Passkeys</iLabel>
      <iText v-if="passkeys.length === 0" variant="subtle">
        No passkeys registered. Add a passkey to use Face ID, Touch ID, or a security key such as a YubiKey.
      </iText>
      <iColumn v-else gap="small">
        <iRow
          v-for="passkey in passkeys"
          :key="passkey.id"
          vertical-align="middle"
          wrap="nowrap"
          width="fill"
        >
          <iColumn gap="none" width="fill">
            <iText font-weight="dark">
              {{ passkey.alias }}
            </iText>
            <iText variant="subtle" font-size="small">
              {{ passkey.last_used > 0 ? `Last used ${formatDate(passkey.last_used)}` : "Never used" }}
            </iText>
          </iColumn>
          <iButton variant="tertiary" size="small" @click="openRenameModal(passkey)">
            Rename
          </iButton>
          <iColumn v-tooltip="factorLockedReason" width="hug">
            <iButton
              variant="tertiary"
              size="small"
              :disabled="!canRemoveFactor"
              @click="removePasskeyFactor(passkey.id)"
            >
              Remove
            </iButton>
          </iColumn>
        </iRow>
      </iColumn>
      <iRow width="hug">
        <iButton variant="primary" :is-loading="isLoading" @click="openAddPasskeyModal">
          Add a passkey
        </iButton>
      </iRow>
    </iColumn>

    <iColumn gap="standard">
      <iLabel>Backup codes</iLabel>
      <iText variant="subtle">
        {{ bypassCodesRemaining }} unused backup {{ bypassCodesRemaining === 1 ? "code" : "codes" }} remaining.
        Each code can be used once if you lose access to your other methods.
      </iText>
      <iRow width="hug">
        <iButton variant="secondary" :is-loading="isLoading" @click="generateBackupCodes">
          {{ bypassCodesRemaining > 0 ? "Regenerate backup codes" : "Generate backup codes" }}
        </iButton>
      </iRow>
    </iColumn>
  </iColumn>

  <iModal
    v-if="totpModalVisible"
    title="Set up authenticator app"
    :max-width="520"
    :focus-first-element="false"
    primary-action-button-label="Confirm"
    @close="closeTotpModal"
    @click:primary="submitTotpConfirm"
  >
    <template #body>
      <iColumn
        gap="large"
        align="center"
        :max-width="520"
        width="fill"
      >
        <iText variant="subtle">
          Scan this QR code with your authenticator app.
        </iText>
        <iImageContainer
          v-if="totpEnrollQrDataUri"
          :src="totpEnrollQrDataUri"
          alt="Authenticator QR code"
          :width="200"
          :height="200"
        />
        <iText variant="subtle" font-size="small">
          Or enter this key manually:
        </iText>
        <iText font-weight="dark">
          {{ totpEnrollSecret }}
        </iText>
        <iTextInput
          v-model="totpCode"
          label="Enter the 6-digit code"
          width="fill"
          inputmode="numeric"
          @enter="submitTotpConfirm"
        />
        <iText v-if="errorMessage" variant="error">
          {{ errorMessage }}
        </iText>
      </iColumn>
    </template>
  </iModal>

  <iModal
    v-if="addPasskeyModalVisible"
    title="Add a passkey"
    :max-width="480"
    primary-action-button-label="Continue"
    @close="closeAddPasskeyModal"
    @click:primary="submitAddPasskey"
  >
    <template #body>
      <iColumn gap="large" :max-width="480" width="fill">
        <iText variant="subtle">
          Give this passkey a name so you can recognise it later, then follow your browser's prompt.
        </iText>
        <iTextInput
          v-model="newPasskeyAlias"
          label="Passkey name"
          placeholder="e.g. MacBook Touch ID, YubiKey"
          width="fill"
          @enter="submitAddPasskey"
        />
        <iText v-if="errorMessage" variant="error">
          {{ errorMessage }}
        </iText>
      </iColumn>
    </template>
  </iModal>

  <iModal
    v-if="renameModalVisible"
    title="Rename passkey"
    :max-width="480"
    primary-action-button-label="Save"
    @close="closeRenameModal"
    @click:primary="submitRename"
  >
    <template #body>
      <iColumn gap="large" :max-width="480" width="fill">
        <iTextInput
          v-model="renameAlias"
          label="Passkey name"
          width="fill"
          @enter="submitRename"
        />
        <iText v-if="errorMessage" variant="error">
          {{ errorMessage }}
        </iText>
      </iColumn>
    </template>
  </iModal>

  <iModal
    v-if="bypassCodesModalVisible"
    title="Your backup codes"
    :max-width="480"
    primary-action-button-label="Done"
    @close="closeBypassCodesModal"
    @click:primary="closeBypassCodesModal"
  >
    <template #body>
      <iColumn gap="large" :max-width="480" width="fill">
        <iText variant="warning">
          Save these codes somewhere safe. They will not be shown again.
        </iText>
        <iColumn gap="small">
          <iText v-for="code in generatedBypassCodes" :key="code" font-weight="dark">
            {{ code }}
          </iText>
        </iColumn>
      </iColumn>
    </template>
  </iModal>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { useSecurityStore } from "@/stores/security-store";

export default {
  name: "UserSecurityManagement",
  data() {
    return {
      totpModalVisible: false,
      totpCode: "",
      addPasskeyModalVisible: false,
      newPasskeyAlias: "",
      renameModalVisible: false,
      renameAlias: "",
      renameTargetId: 0,
      bypassCodesModalVisible: false,
    };
  },
  computed: {
    ...mapState(useSecurityStore, [
      "isLoading",
      "errorMessage",
      "totpEnabled",
      "passkeys",
      "bypassCodesRemaining",
      "mfaRequired",
      "canRemoveFactor",
      "enrolledMethods",
      "totpEnrollSecret",
      "totpEnrollQrDataUri",
      "generatedBypassCodes",
    ]),
    // Empty string is a no-op for v-tooltip, so this only speaks up while removal is blocked.
    factorLockedReason() {
      if (this.canRemoveFactor) {
        return "";
      }
      return "Your account requires two-factor authentication. Set up another method before removing this one.";
    },
  },
  mounted() {
    this.fetchStatus();
  },
  methods: {
    ...mapActions(useSecurityStore, [
      "fetchStatus",
      "beginTotpEnroll",
      "confirmTotp",
      "disableTotp",
      "addPasskey",
      "renamePasskey",
      "removePasskey",
      "generateBypassCodes",
      "clearGeneratedBypassCodes",
    ]),
    formatDate(unixSeconds) {
      return new Date(unixSeconds * 1000).toLocaleDateString();
    },
    // iButton's disabled prop only styles the button — it still emits click — so the guard lives here
    // as well as on the server, which is the real enforcement.
    disableAuthenticatorApp() {
      if (!this.canRemoveFactor) {
        return null;
      }
      return this.disableTotp();
    },
    removePasskeyFactor(passkeyId) {
      if (!this.canRemoveFactor) {
        return null;
      }
      return this.removePasskey(passkeyId);
    },
    openTotpModal() {
      this.totpCode = "";
      this.totpModalVisible = true;
      this.beginTotpEnroll();
    },
    closeTotpModal() {
      this.totpModalVisible = false;
    },
    async submitTotpConfirm() {
      if (this.totpCode.replace(/\D/g, "").length < 6) {
        return;
      }
      await this.confirmTotp(this.totpCode);
      if (this.totpEnabled) {
        this.totpModalVisible = false;
        await this.maybeOfferBackupCodes();
      }
    },
    // First factor with no recovery codes yet — mint + reveal them so the user always has a fallback.
    async maybeOfferBackupCodes() {
      if (this.bypassCodesRemaining === 0 && (this.totpEnabled || this.passkeys.length > 0)) {
        await this.generateBypassCodes();
        if (!this.errorMessage) {
          this.bypassCodesModalVisible = true;
        }
      }
    },
    openAddPasskeyModal() {
      this.newPasskeyAlias = "";
      this.addPasskeyModalVisible = true;
    },
    closeAddPasskeyModal() {
      this.addPasskeyModalVisible = false;
    },
    async submitAddPasskey() {
      await this.addPasskey(this.newPasskeyAlias || "Passkey");
      if (!this.errorMessage) {
        this.addPasskeyModalVisible = false;
      }
    },
    openRenameModal(passkey) {
      this.renameTargetId = passkey.id;
      this.renameAlias = passkey.alias;
      this.renameModalVisible = true;
    },
    closeRenameModal() {
      this.renameModalVisible = false;
    },
    async submitRename() {
      await this.renamePasskey(this.renameTargetId, this.renameAlias || "Passkey");
      if (!this.errorMessage) {
        this.renameModalVisible = false;
      }
    },
    async generateBackupCodes() {
      await this.generateBypassCodes();
      if (!this.errorMessage) {
        this.bypassCodesModalVisible = true;
      }
    },
    closeBypassCodesModal() {
      this.bypassCodesModalVisible = false;
      this.clearGeneratedBypassCodes();
    },
  },
};
</script>

<style lang="scss" scoped></style>
